import { z } from "zod";
import { useI18n } from "./useI18n";

export const useRules = () => {
  const { t } = useI18n();

  const required = (value: unknown) => {
    const { success } = z.union([z.string().trim().min(1), z.number()]).safeParse(value);
    return success || t("errors.required");
  };

  const requiredFile = (value: File | File[] | null) => {
    if (value instanceof File) return true;
    if (Array.isArray(value) && value.length > 0) return true;
    return t("errors.required");
  };

  const validEmail = (value: unknown) => {
    if (!value) return true;
    const { success } = z.email().safeParse(value);
    return success || t("errors.invalidEmail");
  };

  const validPhoneNumber = (value: unknown) => {
    if (!value) return true;
    const { success } = z.e164().safeParse(value);
    if (success) return true;
    if (typeof value === "string" && !value.startsWith("+"))
      return t("errors.phoneNumberMissingCountryCode");
    return t("errors.invalidPhoneNumber");
  };

  const maxFileSize = (maxFileSizeMB: number) => (file: File | undefined) => {
    if (!file) return true;
    return file.size <= maxFileSizeMB * 1024 * 1024 || t("errors.fileTooBig", { maxFileSizeMB });
  };

  const positiveNumber = (value: unknown) => {
    if (!value && value !== 0) return true;
    const { success } = z.coerce.number().positive().safeParse(value);
    return success || t("errors.amountMustBePositive");
  };

  const nonnegativeNumber = (value: unknown) => {
    if (!value && value !== 0) return true;
    const { success } = z.coerce.number().nonnegative().safeParse(value);
    return success || t("errors.amountMustBePositive");
  };

  const maxNumber = (max: number) => (value: unknown) => {
    if (!value && value !== 0) return true;
    const { success } = z.coerce.number().max(max).safeParse(value);
    return success || t("errors.maxNumber", { max });
  };

  const minLength = (min: number) => (value: unknown) => {
    if (!value) return true;
    const { success } = z.string().trim().min(min).safeParse(value);
    return success || t("errors.minLength", { min });
  };

  const dateInPast = (value: unknown) => {
    if (!value) return true;
    return (typeof value === "string" && new Date(value) < new Date()) || t("errors.dateInPast");
  };

  const maxLength = (max: number) => (value: unknown) => {
    if (!value) return true;
    const { success } = z.string().trim().max(max).safeParse(value);
    return success || t("errors.maxLength", { max });
  };

  return {
    required,
    requiredFile,
    validEmail,
    validPhoneNumber,
    maxFileSize,
    positiveNumber,
    nonnegativeNumber,
    maxNumber,
    minLength,
    maxLength,
    dateInPast,
  };
};
